/**
 * The save/restore protocol every emphasis layer shares: stash an element's current attribute
 * values into `data-sb*` dataset slots before overwriting them, then put them back afterwards.
 *
 * Extracted because the same ~20-line block was hand-written once per attribute group
 * (dim, highlight ring, volume-selection ring, instance-selection ring).
 */

export interface AttrSlot {
  /** The DOM attribute this slot stands in for. */
  attr: string;
  /**
   * What an empty saved value means on restore.
   *
   * `'remove'` — the attribute was absent when saved, so remove it again.
   * `'set'`    — write the empty string back verbatim.
   *
   * `'set'` exists only to preserve an existing asymmetry: the highlight / volume-selection /
   * instance-selection restores write `stroke` and `stroke-width` back unconditionally, while
   * doing empty→remove for `stroke-opacity` and `fill`. A circle that had no `stroke` before
   * being highlighted therefore ends up with `stroke=""`. That is very likely a latent bug,
   * but it is pre-existing behaviour and is preserved here deliberately rather than fixed.
   */
  emptyMeans: 'remove' | 'set';
}

/** Dataset key (e.g. `sbDimFill`) → the attribute it stands in for. */
export type AttrSlotMap = Record<string, AttrSlot>;

export const dimCircleSlots: AttrSlotMap = {
  sbDimFill: { attr: 'fill', emptyMeans: 'remove' },
  sbDimStroke: { attr: 'stroke', emptyMeans: 'remove' },
  sbDimFillOpacity: { attr: 'fill-opacity', emptyMeans: 'remove' },
  sbDimStrokeOpacity: { attr: 'stroke-opacity', emptyMeans: 'remove' },
  sbDimFilter: { attr: 'filter', emptyMeans: 'remove' },
};

export const highlightRingSlots: AttrSlotMap = {
  sbStroke: { attr: 'stroke', emptyMeans: 'set' },
  sbWidth: { attr: 'stroke-width', emptyMeans: 'set' },
  sbOpacity: { attr: 'stroke-opacity', emptyMeans: 'remove' },
  sbFill: { attr: 'fill', emptyMeans: 'remove' },
};

export const volumeRingSlots: AttrSlotMap = {
  sbVolumeStroke: { attr: 'stroke', emptyMeans: 'set' },
  sbVolumeWidth: { attr: 'stroke-width', emptyMeans: 'set' },
  sbVolumeOpacity: { attr: 'stroke-opacity', emptyMeans: 'remove' },
  sbVolumeFill: { attr: 'fill', emptyMeans: 'remove' },
};

export const instanceRingSlots: AttrSlotMap = {
  sbInstanceStroke: { attr: 'stroke', emptyMeans: 'set' },
  sbInstanceWidth: { attr: 'stroke-width', emptyMeans: 'set' },
  sbInstanceOpacity: { attr: 'stroke-opacity', emptyMeans: 'remove' },
};

/** Save the element's current values for every slot in `map` into its dataset. */
export function saveAttrs(el: Element, host: { dataset: DOMStringMap }, map: AttrSlotMap): void {
  Object.entries(map).forEach(([slot, { attr }]) => {
    host.dataset[slot] = el.getAttribute(attr) ?? '';
  });
}

/**
 * Restore every slot in `map` onto the element and clear the dataset slots.
 * Slots that were never saved (`undefined`) are left alone.
 */
export function restoreAttrs(
  el: Element,
  host: { dataset: DOMStringMap },
  map: AttrSlotMap
): void {
  Object.entries(map).forEach(([slot, { attr, emptyMeans }]) => {
    const saved = host.dataset[slot];
    if (saved != null) {
      if (saved === '' && emptyMeans === 'remove') el.removeAttribute(attr);
      else el.setAttribute(attr, saved);
    }
    delete host.dataset[slot];
  });
}

/** Clear dataset slots without touching the element's attributes. */
export function clearSlots(host: { dataset: DOMStringMap }, map: AttrSlotMap): void {
  Object.keys(map).forEach((slot) => {
    delete host.dataset[slot];
  });
}
